import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { memo, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { LongTextView } from '../../../../../../components/LongTextView';
import { formatDate } from '../../../utils';
import { FormAutocomplete } from '../../components/Autocomplete';
import { FormDatePicker } from '../../components/FormDatePicker';
import { FormItem } from '../../components/FormItem';
import { MultiFormTemplate } from '../../components/FormTemplates';
import { EmptyFormBlockWrapper } from '../../components/FormTemplates/EmptyFormBlockWrapper';
import { FormRow } from '../../components/FormTemplates/FormRow';
import { NotesField } from '../../components/NotesField';
import { DEBOUNCE_WAIT, EMPTY_PLACEHOLDER } from '../../constants';
import { useFormContext } from '../../context/FormsContext';
import { useFormsStore } from '../../store';
import { CaseFormComponentBaseType, wasCaseFormSaved } from '../../utils/sectionsStateStorageUtils';
import { Officers } from './Officers';
import { officerSchema } from './Officers/Officer';
import { SeizedCommodities } from './SeizedCommodities';
import { seizedCommoditySchema } from './SeizedCommodities/SeizedCommodity';
import styles from './Seizures.module.css';

export const schema = z.object({
    id: z.coerce.string().optional(),
    date: z.coerce.date().nullish(),
    time: z.string().nullish(),
    locationName: z.string().nullish(),
    streetAddress: z.string().nullish(),
    administrativeLevel1Id: z.string().nullish(),
    administrativeLevel2Id: z.string().nullish(),
    notes: z.string().nullish(),
    seizedCommodities: z.array(seizedCommoditySchema),
    officers: z.array(officerSchema),
    disconnectOfficerIds: z.string().array().nullish(),
    locationGeometry: z.string().nullish(),
});

type SeizureType = z.infer<typeof schema>;

const seizureDefaults: SeizureType = {
    date: null,
    time: null,
    locationName: null,
    streetAddress: null,
    administrativeLevel1Id: null,
    administrativeLevel2Id: null,
    notes: null,
    seizedCommodities: [],
    officers: [],
    disconnectOfficerIds: null,
    locationGeometry: null,
};
interface SeizureProps extends CaseFormComponentBaseType {
    defaultValues?: SeizureType;
    onChange?: (result: SeizureType, updateStatus?: boolean) => void;
    onDelete?: () => void;
    onValid?: (isValid: boolean) => void;
}

export const Seizure = memo(({ defaultValues, onChange, onDelete: propOnDelete, onValid }: SeizureProps) => {
    const { onSubmitListener } = useFormContext();
    const administrativeLevel1s = useFormsStore((state) => state.lookups.administrativeLevel1s);
    const administrativeLevel2s = useFormsStore((state) => state.lookups.administrativeLevel2s);
    const lookupsIsLoading = useFormsStore((state) => state.lookupsIsLoading);
    const contextEditMode = useFormsStore((state) => state.editMode);
    const [initialValue] = useState({
        ...seizureDefaults,
        ...defaultValues,
    });

    const {
        control,
        formState: { errors },
        handleSubmit,
        setValue,
        watch,
        reset,
    } = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: initialValue,
    });

    const selectedAdministrativeLevel1Id = watch('administrativeLevel1Id');

    const administrativeLevel2Options = useMemo(() => {
        if (!selectedAdministrativeLevel1Id) {
            return administrativeLevel2s.values;
        }

        return administrativeLevel2s.values.filter(
            ({ administrativeLevel1 }) => administrativeLevel1?.id === selectedAdministrativeLevel1Id
        );
    }, [administrativeLevel2s, selectedAdministrativeLevel1Id]);

    const [disconnectOfficerIds, setDisconnectOfficerIds] = useState<string[]>([]);

    useEffect(() => {
        reset({
            ...seizureDefaults,
            ...defaultValues,
        });
    }, [defaultValues, reset]);

    useEffect(() => {
        setValue('disconnectOfficerIds', disconnectOfficerIds);
    }, [disconnectOfficerIds]);

    useEffect(() => {
        const errorsCount = Object.keys(errors).length;
        onValid?.(errorsCount === 0);
    }, [JSON.stringify(errors)]);

    useEffect(() => {
        let currentData: SeizureType;
        const onChangeDebounce = debounce(() => {
            onChange && onChange(currentData);
        }, DEBOUNCE_WAIT);

        const { unsubscribe } = watch((data) => {
            currentData = data as SeizureType;
            onChangeDebounce();
        });

        return unsubscribe;
    }, [watch]);

    useEffect(() => {
        return onSubmitListener('seizures', () => {
            return new Promise((resolve) => {
                handleSubmit(
                    (data) => {
                        resolve({ type: 'payload', result: data });
                    },
                    () => {
                        resolve({ type: 'payload', result: false });
                    }
                )();
            });
        });
    }, []);

    const editMode = useMemo(() => contextEditMode === 'edit', [contextEditMode]);

    const getAdministrativeLevel1 = (levelId?: string | null) => {
        return administrativeLevel1s.values.find((item) => item.id === levelId);
    };

    const getAdministrativeLevel2 = (levelId?: string | null) => {
        return administrativeLevel2s.values.find((item) => item.id === levelId);
    };

    const onDelete = () => {
        propOnDelete && propOnDelete();
    };

    const onDisconnectOfficer = (officerId: string) => {
        setDisconnectOfficerIds(disconnectOfficerIds.filter((id) => id !== officerId).concat([officerId]));
    };

    return (
        <MultiFormTemplate
            editMode={editMode}
            headerButtonIsAbsolute
            FormBlockWrapper={EmptyFormBlockWrapper}
            header={
                <>
                    <Box sx={{ width: '100%' }}>
                        <Typography className={styles.subsectionTitle}>Event Details</Typography>
                    </Box>
                    <FormRow>
                        <Controller
                            name="date"
                            control={control}
                            render={({ field }) => (
                                <FormItem
                                    errorMessage={errors.date?.message}
                                    editMode={editMode}
                                    label="Date"
                                    value={field.value}
                                    renderValue={formatDate}
                                    renderInput={(value) => (
                                        <FormDatePicker
                                            errorMessage={errors.date?.message}
                                            value={value}
                                            onChange={(value) => {
                                                field.onChange({ target: { value } });
                                            }}
                                        />
                                    )}
                                />
                            )}
                        />
                        <Controller
                            name="locationName"
                            control={control}
                            render={({ field }) => (
                                <FormItem
                                    errorMessage={errors?.locationName?.message}
                                    className={styles.headerField}
                                    editMode={editMode}
                                    label="Location"
                                    value={field.value}
                                    renderValue={(value) => value || EMPTY_PLACEHOLDER}
                                    onChange={(value) => {
                                        field.onChange({
                                            target: { value },
                                        });
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="streetAddress"
                            control={control}
                            render={({ field }) => (
                                <FormItem
                                    errorMessage={errors?.streetAddress?.message}
                                    editMode={editMode}
                                    label="Street Address"
                                    value={field.value}
                                    renderValue={(value) => value || EMPTY_PLACEHOLDER}
                                    onChange={(value) => {
                                        field.onChange({
                                            target: { value },
                                        });
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="administrativeLevel1Id"
                            control={control}
                            render={({ field }) => (
                                <FormItem
                                    errorMessage={errors?.administrativeLevel1Id?.message}
                                    isLoading={lookupsIsLoading}
                                    editMode={editMode}
                                    label={administrativeLevel1s.localName}
                                    value={field.value}
                                    onChange={(value) => {
                                        field.onChange({
                                            target: { value },
                                        });
                                    }}
                                    renderValue={(value) => getAdministrativeLevel1(value)?.name || EMPTY_PLACEHOLDER}
                                    renderInput={(value) => (
                                        <FormAutocomplete
                                            disablePortal={false}
                                            errorMessage={errors?.administrativeLevel1Id?.message}
                                            label={null}
                                            options={administrativeLevel1s.values}
                                            getOptionLabel={(option) => option.name || EMPTY_PLACEHOLDER}
                                            value={getAdministrativeLevel1(value)}
                                            onChange={(_e, newValue) => {
                                                field.onChange({ target: { value: newValue?.id } });
                                            }}
                                        />
                                    )}
                                />
                            )}
                        />
                        <Controller
                            name="administrativeLevel2Id"
                            control={control}
                            render={({ field }) => (
                                <FormItem
                                    errorMessage={errors?.administrativeLevel2Id?.message}
                                    isLoading={lookupsIsLoading}
                                    editMode={editMode}
                                    label={administrativeLevel2s.localName}
                                    value={field.value}
                                    onChange={(value) => {
                                        field.onChange({
                                            target: { value },
                                        });
                                    }}
                                    renderValue={(value) => getAdministrativeLevel2(value)?.name || EMPTY_PLACEHOLDER}
                                    renderInput={(value) => (
                                        <FormAutocomplete
                                            disablePortal={false}
                                            errorMessage={errors?.administrativeLevel2Id?.message}
                                            label={null}
                                            options={administrativeLevel2Options}
                                            getOptionLabel={(option) => option.name || EMPTY_PLACEHOLDER}
                                            value={getAdministrativeLevel2(value)}
                                            onChange={(_e, newValue) => {
                                                field.onChange({ target: { value: newValue?.id } });
                                            }}
                                        />
                                    )}
                                />
                            )}
                        />
                    </FormRow>
                </>
            }
            formBlocks={[
                <Controller
                    key="seizedCommodities"
                    name="seizedCommodities"
                    control={control}
                    render={({ field }) => (
                        <SeizedCommodities
                            onChange={(value) => {
                                field.onChange({
                                    target: {
                                        value,
                                    },
                                });
                            }}
                            editMode={editMode}
                            defaultValues={defaultValues?.seizedCommodities}
                        />
                    )}
                />,
                <Controller
                    key="officers"
                    name="officers"
                    control={control}
                    render={({ field }) => (
                        <Officers
                            onChange={(value) => {
                                field.onChange({
                                    target: {
                                        value,
                                    },
                                });
                            }}
                            editMode={editMode}
                            defaultValues={defaultValues?.officers ?? []}
                            onDisconnectOfficer={onDisconnectOfficer}
                        />
                    )}
                />,
            ]}
            notes={
                <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                        <FormItem
                            errorMessage={errors.notes?.message}
                            className={styles.notesFormItem}
                            editMode={editMode}
                            label="Additional Notes:"
                            value={field.value}
                            renderValue={(value) => <LongTextView>{value || ''}</LongTextView>}
                            renderInput={(value) => (
                                <NotesField
                                    onChange={(e) => {
                                        field.onChange({
                                            target: { value: e.target.value },
                                        });
                                    }}
                                    value={value}
                                    errorMessage={errors.notes?.message}
                                />
                            )}
                        />
                    )}
                />
            }
            onDelete={onDelete}
        />
    );
}, wasCaseFormSaved);
